// ============================================
// ImageEditor.jsx - Editor de Imagem Estilo Lightshot
// Salve este arquivo em: src/components/ImageEditor.jsx
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import {
    X, Check, Square, Circle, Type, Minus, ArrowRight,
    Pencil, Undo, Redo, RotateCw, Move, ChevronDown
} from 'lucide-react';

const ImageEditor = ({ image, onSave, onCancel }) => {
    const canvasRef = useRef(null);
    const [ctx, setCtx] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [tool, setTool] = useState('select');
    const [color, setColor] = useState('#FF0000');
    const [lineWidth, setLineWidth] = useState(3);
    const [history, setHistory] = useState([]);
    const [historyStep, setHistoryStep] = useState(-1);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [textInput, setTextInput] = useState('');
    const [textPosition, setTextPosition] = useState(null);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [caption, setCaption] = useState('');
    const [endPos, setEndPos] = useState({ x: 0, y: 0 }); // Posição final para formas
    const [isSending, setIsSending] = useState(false); // Payload state

    const colors = [
        '#FF0000', '#FF6B00', '#FFE600', '#00FF00', '#00D4FF',
        '#0066FF', '#9D00FF', '#FF00DC', '#000000', '#FFFFFF',
        '#808080', '#C0C0C0'
    ];

    const lineWidths = [2, 3, 5, 8, 12];

    // Inicializar canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        setCtx(context);

        const img = new Image();
        img.onload = () => {
            const maxWidth = window.innerWidth * 0.9;
            const maxHeight = window.innerHeight * 0.75;

            let width = img.width;
            let height = img.height;

            if (width > maxWidth) {
                height = (maxWidth / width) * height;
                width = maxWidth;
            }

            if (height > maxHeight) {
                width = (maxHeight / height) * width;
                height = maxHeight;
            }

            canvas.width = img.width;
            canvas.height = img.height;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;

            context.drawImage(img, 0, 0);

            const initialState = canvas.toDataURL();
            setHistory([initialState]);
            setHistoryStep(0);
        };

        if (typeof image === 'string') {
            img.src = image;
        } else if (image instanceof File || image instanceof Blob) {
            const reader = new FileReader();
            reader.onload = (e) => {
                img.src = e.target.result;
            };
            reader.readAsDataURL(image);
        }
    }, [image]);

    // Atalhos de teclado
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey || e.metaKey) {
                if (e.key === 'z') {
                    e.preventDefault();
                    handleUndo();
                } else if (e.key === 'y') {
                    e.preventDefault();
                    handleRedo();
                }
            } else {
                const key = e.key.toLowerCase();
                const toolMap = {
                    'v': 'select',
                    'p': 'pencil',
                    'l': 'line',
                    'a': 'arrow',
                    'r': 'rectangle',
                    'c': 'circle',
                    't': 'text'
                };
                if (toolMap[key]) {
                    setTool(toolMap[key]);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [historyStep, history.length]);

    const saveToHistory = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const newHistory = history.slice(0, historyStep + 1);
        newHistory.push(canvas.toDataURL());
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);
    };

    const restoreFromHistory = (step) => {
        const canvas = canvasRef.current;
        if (!canvas || !history[step]) return;

        const img = new Image();
        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
        };
        img.src = history[step];
    };

    const handleUndo = () => {
        if (historyStep > 0) {
            const newStep = historyStep - 1;
            setHistoryStep(newStep);
            restoreFromHistory(newStep);
        }
    };

    const handleRedo = () => {
        if (historyStep < history.length - 1) {
            const newStep = historyStep + 1;
            setHistoryStep(newStep);
            restoreFromHistory(newStep);
        }
    };

    const handleRotate = () => {
        const canvas = canvasRef.current;
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');

        tempCanvas.width = canvas.height;
        tempCanvas.height = canvas.width;

        tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
        tempCtx.rotate((90 * Math.PI) / 180);
        tempCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

        canvas.width = tempCanvas.width;
        canvas.height = tempCanvas.height;
        ctx.drawImage(tempCanvas, 0, 0);

        saveToHistory();
    };

    const getMousePos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    };

    const drawArrow = (context, fromX, fromY, toX, toY, color, width) => {
        const headLength = width * 4;
        const angle = Math.atan2(toY - fromY, toX - fromX);

        context.strokeStyle = color;
        context.fillStyle = color;
        context.lineWidth = width;
        context.lineCap = 'round';
        context.lineJoin = 'round';

        context.beginPath();
        context.moveTo(fromX, fromY);
        context.lineTo(toX, toY);
        context.stroke();

        context.beginPath();
        context.moveTo(toX, toY);
        context.lineTo(
            toX - headLength * Math.cos(angle - Math.PI / 7),
            toY - headLength * Math.sin(angle - Math.PI / 7)
        );
        context.lineTo(
            toX - headLength * Math.cos(angle + Math.PI / 7),
            toY - headLength * Math.sin(angle + Math.PI / 7)
        );
        context.closePath();
        context.fill();
    };

    const handleMouseDown = (e) => {
        if (tool === 'text') {
            const pos = getMousePos(e);
            setTextPosition(pos);
            return;
        }

        setIsDrawing(true);
        const pos = getMousePos(e);
        setStartPos(pos);

        if (tool === 'pencil') {
            ctx.beginPath();
            ctx.moveTo(pos.x, pos.y);
            ctx.strokeStyle = color;
            ctx.lineWidth = lineWidth;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        }
    };

    const handleMouseMove = (e) => {
        if (!isDrawing) return;

        const pos = getMousePos(e);
        setEndPos(pos); // Armazena posição final

        if (tool === 'pencil') {
            ctx.lineTo(pos.x, pos.y);
            ctx.stroke();
        } else {
            restoreFromHistory(historyStep);

            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = lineWidth;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            if (tool === 'rectangle') {
                const width = pos.x - startPos.x;
                const height = pos.y - startPos.y;
                ctx.strokeRect(startPos.x, startPos.y, width, height);
            } else if (tool === 'circle') {
                const radius = Math.sqrt(
                    Math.pow(pos.x - startPos.x, 2) +
                    Math.pow(pos.y - startPos.y, 2)
                );
                ctx.beginPath();
                ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI);
                ctx.stroke();
            } else if (tool === 'line') {
                ctx.beginPath();
                ctx.moveTo(startPos.x, startPos.y);
                ctx.lineTo(pos.x, pos.y);
                ctx.stroke();
            } else if (tool === 'arrow') {
                drawArrow(ctx, startPos.x, startPos.y, pos.x, pos.y, color, lineWidth);
            }
        }
    };

    const handleMouseUp = () => {
        if (!isDrawing) return;
        setIsDrawing(false);

        if (tool === 'pencil') {
            ctx.closePath();
        } else if (tool !== 'select') {
            // Redesenha a forma final para garantir que seja salva
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = lineWidth;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            if (tool === 'rectangle') {
                const width = endPos.x - startPos.x;
                const height = endPos.y - startPos.y;
                ctx.strokeRect(startPos.x, startPos.y, width, height);
            } else if (tool === 'circle') {
                const radius = Math.sqrt(
                    Math.pow(endPos.x - startPos.x, 2) +
                    Math.pow(endPos.y - startPos.y, 2)
                );
                ctx.beginPath();
                ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI);
                ctx.stroke();
            } else if (tool === 'line') {
                ctx.beginPath();
                ctx.moveTo(startPos.x, startPos.y);
                ctx.lineTo(endPos.x, endPos.y);
                ctx.stroke();
            } else if (tool === 'arrow') {
                drawArrow(ctx, startPos.x, startPos.y, endPos.x, endPos.y, color, lineWidth);
            }
        }

        saveToHistory();
    };

    const handleAddText = () => {
        if (!textInput || !textPosition) return;

        const fontSize = lineWidth * 6;
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.fillStyle = color;
        ctx.textBaseline = 'top';

        const metrics = ctx.measureText(textInput);
        const textHeight = fontSize;

        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.fillRect(
            textPosition.x - 4,
            textPosition.y - 4,
            metrics.width + 8,
            textHeight + 8
        );

        ctx.fillStyle = color;
        ctx.fillText(textInput, textPosition.x, textPosition.y);

        saveToHistory();
        setTextInput('');
        setTextPosition(null);
        setTool('select');
    };

    const handleSave = () => {
        if (isSending) return;
        setIsSending(true);
        const canvas = canvasRef.current;
        canvas.toBlob(async (blob) => {
            const file = new File([blob], 'image-edited.png', { type: 'image/png' });
            try {
                await onSave(file, caption);
            } catch (error) {
                console.error("Erro ao enviar imagem:", error);
            } finally {
                setIsSending(false);
            }
        }, 'image/png', 1.0);
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col">
            {/* Toolbar */}
            <div className="bg-gray-900 border-b border-gray-700 p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h3 className="text-white font-bold text-lg">Editor de Imagem</h3>

                    <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
                        <button onClick={() => setTool('select')} className={`p-2 rounded transition-colors ${tool === 'select' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Mover (V)">
                            <Move className="w-5 h-5" />
                        </button>
                        <button onClick={() => setTool('pencil')} className={`p-2 rounded transition-colors ${tool === 'pencil' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Desenhar (P)">
                            <Pencil className="w-5 h-5" />
                        </button>
                        <button onClick={() => setTool('line')} className={`p-2 rounded transition-colors ${tool === 'line' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Linha (L)">
                            <Minus className="w-5 h-5" />
                        </button>
                        <button onClick={() => setTool('arrow')} className={`p-2 rounded transition-colors ${tool === 'arrow' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Seta (A)">
                            <ArrowRight className="w-5 h-5" />
                        </button>
                        <button onClick={() => setTool('rectangle')} className={`p-2 rounded transition-colors ${tool === 'rectangle' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Retângulo (R)">
                            <Square className="w-5 h-5" />
                        </button>
                        <button onClick={() => setTool('circle')} className={`p-2 rounded transition-colors ${tool === 'circle' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Círculo (C)">
                            <Circle className="w-5 h-5" />
                        </button>
                        <button onClick={() => setTool('text')} className={`p-2 rounded transition-colors ${tool === 'text' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Texto (T)">
                            <Type className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="relative">
                        <button onClick={() => setShowColorPicker(!showColorPicker)} className="flex items-center gap-2 bg-gray-800 px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors">
                            <div className="w-6 h-6 rounded border-2 border-white" style={{ backgroundColor: color }} />
                            <ChevronDown className="w-4 h-4 text-gray-300" />
                        </button>

                        {showColorPicker && (
                            <div className="absolute top-full mt-2 bg-gray-800 p-3 rounded-lg shadow-xl z-10 grid grid-cols-6 gap-2">
                                {colors.map((c) => (
                                    <button key={c} onClick={() => { setColor(c); setShowColorPicker(false); }} className={`w-8 h-8 rounded border-2 transition-transform hover:scale-110 ${color === c ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-600'}`} style={{ backgroundColor: c }} />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2 bg-gray-800 px-3 py-2 rounded-lg">
                        <span className="text-gray-300 text-sm">Espessura:</span>
                        <div className="flex gap-1">
                            {lineWidths.map((width) => (
                                <button key={width} onClick={() => setLineWidth(width)} className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${lineWidth === width ? 'bg-blue-600' : 'hover:bg-gray-700'}`}>
                                    <div className="rounded-full bg-white" style={{ width: `${width}px`, height: `${width}px` }} />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
                        <button onClick={handleUndo} disabled={historyStep <= 0} className="p-2 rounded text-gray-300 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Desfazer (Ctrl+Z)">
                            <Undo className="w-5 h-5" />
                        </button>
                        <button onClick={handleRedo} disabled={historyStep >= history.length - 1} className="p-2 rounded text-gray-300 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Refazer (Ctrl+Y)">
                            <Redo className="w-5 h-5" />
                        </button>
                        <button onClick={handleRotate} className="p-2 rounded text-gray-300 hover:bg-gray-700 transition-colors" title="Rotacionar 90°">
                            <RotateCw className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button onClick={onCancel} className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2">
                        <X className="w-5 h-5" />
                        Cancelar
                    </button>
                    <button onClick={handleSave} disabled={isSending} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                        {isSending ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <Check className="w-5 h-5" />
                        )}
                        {isSending ? 'Enviando...' : 'Enviar'}
                    </button>
                </div>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-auto gap-4">
                <div className="relative">
                    <canvas ref={canvasRef} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} className="max-w-full max-h-full shadow-2xl" style={{ cursor: tool === 'select' ? 'default' : 'crosshair', imageRendering: 'crisp-edges' }} />
                </div>

                <div className="w-full max-w-3xl bg-gray-800 rounded-lg p-4">
                    <label className="block text-white font-bold mb-2 flex items-center gap-2">
                        <Type className="w-4 h-4" />
                        Legenda / Descrição (Opcional)
                    </label>
                    <input
                        type="text"
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        placeholder="Digite uma legenda para a imagem..."
                        className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none transition-colors"
                    />
                </div>
            </div>

            {textPosition && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-10">
                    <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full">
                        <h4 className="text-white font-bold mb-4">Adicionar Texto</h4>
                        <input type="text" value={textInput} onChange={(e) => setTextInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddText()} placeholder="Digite o texto..." autoFocus className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none mb-4" />
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => { setTextPosition(null); setTextInput(''); }} className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600">Cancelar</button>
                            <button onClick={handleAddText} disabled={!textInput} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">Adicionar</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="absolute bottom-4 left-4 bg-gray-900/90 text-gray-300 text-xs px-3 py-2 rounded-lg">
                <div className="font-bold mb-1">Atalhos:</div>
                <div>V - Mover | P - Desenhar | L - Linha | A - Seta | R - Retângulo | C - Círculo | T - Texto</div>
                <div>Ctrl+Z - Desfazer | Ctrl+Y - Refazer</div>
            </div>
        </div>
    );
};

export default ImageEditor;
